<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </Teleport>

  <Teleport to="body">
  <div class="modal-overlay">
    <div class="modal-container" :class="{ 'modal-reactivate': action === 'reactivate', 'modal-deactivate': action === 'deactivate' }" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">{{ pageTitle }}</h2>
        <button class="close-btn" @click="handleClose" type="button">
          <img :src="fillIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div v-if="itemName" class="modal-body">
        <p class="check-message">
          {{ messageText }}
        </p>
      </div>

      <div v-else class="modal-body">
        <p class="error-text">Item not found</p>
      </div>

      <div class="modal-footer">
        <button class="btn-no" @click="handleClose" type="button">No</button>
        <button class="btn-yes" :disabled="submitting" @click="handleConfirm" type="button">
          <span v-if="submitting" class="spinner"></span>
          <span>{{ submitting ? '' : 'Yes' }}</span>
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { updateChecklistStatus } from '@/api/checkstatus'
import fillIcon from '@/assets/Fill 1@2x.png'
import ToastComponent from '@/components/ToastComponent.vue'

interface Props {
  itemId: string
  action: 'deactivate' | 'reactivate'
  itemName: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
  (e: 'toast', data: { type: 'success' | 'warning' | 'error'; message: string }): void
}>()

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('warning')
const toastMessage = ref('')
const submitting = ref(false)
let toastTimer: ReturnType<typeof setTimeout> | null = null

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
  }, 3500)
}

const pageTitle = computed(() => {
  return props.action === 'deactivate' ? 'Deactivate Check' : 'Reactivate Check'
})

const messageText = computed(() => {
  if (!props.itemName) return ''
  if (props.action === 'deactivate') {
    return `Are you sure you want to deactivate "${props.itemName}"? Existing data will not be affected, but this item will no longer be available going forward. You can reactivate it if needed.`
  } else {
    return `Are you sure you want to reactivate "${props.itemName}"? This item will remain available.`
  }
})

function handleClose(): void {
  emit('close')
}

async function handleConfirm(): Promise<void> {
  submitting.value = true
  try {
    const newStatus = props.action === 'deactivate' ? 0 : 1
    const response = await updateChecklistStatus({
      id: Number(props.itemId),
      status: newStatus,
    })

    if (response.code === 200) {
      emit('saved')
      emit('toast', {
        type: 'success',
        message: props.action === 'deactivate' ? 'Successfully deactivated' : 'Successfully reactivated',
      })
      setTimeout(() => emit('close'), 300)
    } else {
      showToast('error', response.msg || 'Failed to update status')
      setTimeout(() => emit('close'), 300)
    }
  } catch (error) {
    console.error('Update status error:', error)
    showToast('error', 'Failed to update status')
    setTimeout(() => emit('close'), 300)
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 12px;
  width: 530px;
  min-height: 212px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  padding: 24px;
  box-sizing: border-box;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 8px;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  padding: 0 24px 24px;
  box-sizing: border-box;
}

.check-message {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  word-break: break-word;
  overflow-wrap: break-word;

}

.error-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: center;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 0 24px 24px;
  flex-shrink: 0;
  margin-top: auto;
}

.modal-footer{
  z-index: 10000;
}

.btn-no {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 16px;
  background-color: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-no:hover {
  background-color: #000000;
  border-color: #000000 !important;
  color: #ffffff;
  outline: none;
}

.btn-yes {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-yes:hover:not(:disabled) {
  background-color: #484848;
}

.btn-yes:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
